/**
 * Reusable "Import an ETF report" flow (imaging — NOT lab values).
 *
 * Pipeline: file picker / drag-and-drop → POST /attachments (kind
 * 'imaging_report', multipart) → POST /imaging/extract → editable review form
 * pre-filled from the (DRAFT) AI findings summary → POST /appointments with
 * sourceAttachmentId so the report is linked to the new appointment.
 *
 * Imaging reports are free-text clinical documents (findings such as
 * "microcalcifications", ventricle size), so the review is a single findings
 * textarea plus exam metadata — not an analyte table. The AI output is ALWAYS a
 * draft the clinician verifies before saving.
 *
 * App-specific labels that are not in the shared i18n catalog use a tiny inline
 * FR/EN map keyed off the current locale.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { EXAM_TYPES, APPOINTMENT_STATUSES } from '@toxofollow/shared';
import { api, getCsrfToken, ApiError } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Field, inputClass } from './ui.js';
import { Select, Textarea, ErrorState } from './form.js';

// --- Inline bilingual strings (not present in the shared catalog) -----------
const L = {
  pick: { fr: 'Choisir un fichier (PDF ou photo)', en: 'Choose a file (PDF or photo)' },
  drop: { fr: 'Glissez-déposez un compte rendu ici', en: 'Drag & drop a report here' },
  dropOr: { fr: 'ou', en: 'or' },
  dropActive: { fr: 'Déposez pour importer', en: 'Drop to import' },
  unsupportedFile: { fr: 'Type de fichier non pris en charge (PDF ou image).', en: 'Unsupported file type (PDF or image).' },
  uploading: { fr: 'Téléversement…', en: 'Uploading…' },
  extracting: { fr: 'Analyse du compte rendu…', en: 'Reading the report…' },
  draftNotice: { fr: 'Brouillon — à vérifier', en: 'Draft — verify' },
  draftHint: {
    fr: 'Le résumé ci-dessous est un brouillon issu du document (IA). Corrigez-le avant d’enregistrer.',
    en: 'The summary below is a draft generated from the document (AI). Correct it before saving.',
  },
  manualEntry: { fr: 'Saisie manuelle', en: 'Manual entry' },
  manualHint: {
    fr: 'Aucun résumé automatique. Saisissez les conclusions manuellement.',
    en: 'No automatic summary. Enter the findings manually.',
  },
  examType: { fr: 'Type d’examen', en: 'Exam type' },
  examDate: { fr: 'Date de l’examen', en: 'Exam date' },
  specialist: { fr: 'Spécialiste / centre', en: 'Specialist / facility' },
  findings: { fr: 'Conclusions', en: 'Findings' },
  findingsPlaceholder: {
    fr: 'Ex. ventricules de taille normale, pas de microcalcification…',
    en: 'E.g. normal ventricle size, no microcalcification…',
  },
  status: { fr: 'Statut', en: 'Status' },
  nextDate: { fr: 'Prochain examen', en: 'Next exam' },
  submit: { fr: 'Enregistrer', en: 'Save' },
  reset: { fr: 'Recommencer', en: 'Start over' },
  saved: { fr: 'Compte rendu enregistré', en: 'Report saved' },
  importAnother: { fr: 'Importer un autre', en: 'Import another' },
} as const;

interface ImagingExtractResponse {
  summarized: boolean;
  examType?: string;
  examDate?: string;
  specialist?: string;
  findings?: string;
  nextDate?: string;
  source?: 'ai';
}

const today = () => new Date().toISOString().slice(0, 10);

export function ImagingImport({ onDone, className }: { onDone?: () => void; className?: string }) {
  const { t, locale } = useI18n();
  const lang = locale === 'en' ? 'en' : 'fr';
  const tr = (m: { fr: string; en: string }) => m[lang];

  // Phases: pick → review (form shown) → done
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [summarized, setSummarized] = useState(false);
  const [examType, setExamType] = useState<string>('etf');
  const [examDate, setExamDate] = useState(today());
  const [specialist, setSpecialist] = useState('');
  const [findings, setFindings] = useState('');
  const [status, setStatus] = useState('completed');
  const [nextDate, setNextDate] = useState('');
  const [done, setDone] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);

  function resetAll() {
    setAttachmentId(null);
    setSummarized(false);
    setExamType('etf');
    setExamDate(today());
    setSpecialist('');
    setFindings('');
    setStatus('completed');
    setNextDate('');
    setDone(false);
    setDragging(false);
    setDropError(null);
    uploadM.reset();
    saveM.reset();
  }

  // Upload the chosen file then immediately run the imaging summary.
  const uploadM = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', 'imaging_report');
      const res = await fetch('/api/attachments', {
        method: 'POST',
        credentials: 'include',
        headers: { 'x-csrf-token': getCsrfToken() ?? '' },
        body: fd,
      });
      const text = await res.text();
      const json = text ? JSON.parse(text) : undefined;
      if (!res.ok) {
        const err = json?.error ?? {};
        throw new ApiError(res.status, err.code ?? 'upload_failed', err.message ?? res.statusText, err.details);
      }
      const id: string = json.attachment.id;
      const ex = await api.post<ImagingExtractResponse>('/imaging/extract', { attachmentId: id });
      return { id, ex };
    },
    onSuccess: ({ id, ex }) => {
      setAttachmentId(id);
      setSummarized(Boolean(ex.summarized && ex.findings));
      if (ex.examType && (EXAM_TYPES as readonly string[]).includes(ex.examType)) setExamType(ex.examType);
      if (ex.examDate) setExamDate(ex.examDate);
      if (ex.specialist) setSpecialist(ex.specialist);
      if (ex.findings) setFindings(ex.findings);
      if (ex.nextDate) setNextDate(ex.nextDate);
    },
  });

  const saveM = useMutation({
    mutationFn: () =>
      api.post('/appointments', {
        date: examDate,
        examType,
        specialist: specialist.trim() || undefined,
        findings: findings.trim() || undefined,
        status,
        nextDate: nextDate || undefined,
        sourceAttachmentId: attachmentId ?? undefined,
      }),
    onSuccess: () => {
      setDone(true);
      onDone?.();
    },
  });

  function acceptFile(file: File | undefined | null) {
    if (!file) return;
    const ok = file.type === 'application/pdf' || file.type.startsWith('image/');
    if (!ok) {
      setDropError(tr(L.unsupportedFile));
      return;
    }
    setDropError(null);
    uploadM.mutate(file);
  }
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.target.files?.[0]);
    e.target.value = '';
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (!busy) acceptFile(e.dataTransfer.files?.[0]);
  };
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!dragging) setDragging(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
  };

  const hasForm = attachmentId !== null && !done;
  const busy = uploadM.isPending;

  return (
    <div className={className}>
      {/* --- Success state --- */}
      {done && (
        <div className="space-y-3">
          <div className="text-sm rounded-lg px-3 py-2 bg-green-50 border border-green-200 text-green-800" role="status">
            ✓ {tr(L.saved)}
          </div>
          <Button type="button" variant="secondary" onClick={resetAll}>
            {tr(L.importAnother)}
          </Button>
        </div>
      )}

      {/* --- File picker + drag-and-drop (initial) --- */}
      {attachmentId === null && !done && (
        <div className="space-y-2">
          <label
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center cursor-pointer transition ${
              dragging ? 'border-brand bg-brand-light/40' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'
            } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
          >
            <span className="text-2xl" aria-hidden>🧠</span>
            <span className="text-sm font-medium text-slate-700">{dragging ? tr(L.dropActive) : tr(L.drop)}</span>
            <span className="text-xs text-slate-400">{tr(L.dropOr)}</span>
            <span className="text-sm text-brand underline">{tr(L.pick)}</span>
            <input type="file" accept="application/pdf,image/*" onChange={onFile} disabled={busy} className="hidden" />
          </label>
          {busy && <p className="text-sm text-slate-400">{uploadM.isPending ? tr(L.extracting) : tr(L.uploading)}</p>}
          {dropError && <p className="text-sm text-danger">{dropError}</p>}
          {uploadM.isError && <ErrorState error={uploadM.error} fallback={t('common.error')} />}
        </div>
      )}

      {/* --- Review form --- */}
      {hasForm && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveM.mutate();
          }}
          className="space-y-3"
        >
          {summarized ? (
            <div className="text-sm rounded-lg px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800">
              <strong>{tr(L.draftNotice)}</strong>
              <span className="block text-amber-700">{tr(L.draftHint)}</span>
            </div>
          ) : (
            <div className="text-sm rounded-lg px-3 py-2 bg-slate-50 border border-slate-200 text-slate-700">
              <strong>{tr(L.manualEntry)}</strong>
              <span className="block text-slate-500">{tr(L.manualHint)}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
            <Field label={tr(L.examType)}>
              <Select value={examType} onChange={(e) => setExamType(e.target.value)}>
                {EXAM_TYPES.map((x) => (
                  <option key={x} value={x}>{t(`exam.${x}`)}</option>
                ))}
              </Select>
            </Field>
            <Field label={tr(L.examDate)}>
              <input className={inputClass} type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} required />
            </Field>
            <Field label={tr(L.specialist)}>
              <input className={inputClass} value={specialist} onChange={(e) => setSpecialist(e.target.value)} />
            </Field>
            <Field label={tr(L.status)}>
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                {APPOINTMENT_STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Select>
            </Field>
          </div>

          <Field label={tr(L.findings)}>
            <Textarea
              rows={5}
              value={findings}
              placeholder={tr(L.findingsPlaceholder)}
              onChange={(e) => setFindings(e.target.value)}
            />
          </Field>

          <Field label={tr(L.nextDate)}>
            <input className={inputClass} type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} />
          </Field>

          {saveM.isError && <ErrorState error={saveM.error} fallback={t('common.error')} />}

          <div className="flex gap-2">
            <Button type="submit" disabled={saveM.isPending || findings.trim() === ''}>
              {tr(L.submit)}
            </Button>
            <Button type="button" variant="secondary" onClick={resetAll}>
              {tr(L.reset)}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export { L as imagingImportStrings };
